import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ChevronDownIcon from '@/assets/icons/chevron-down.svg';
import { useConfirmation } from '@/components/base/confirmation';
import { TrashIcon } from '@/components/icons/trash';
import { ChannelRow } from '@/components/pages/job-posting/openings-panel/channel-row';
import { useOpening } from '@/components/pages/job-posting/use-opening';
import { useRemoveOpeningFromPosting } from '@/components/pages/job-posting/use-remove-opening-from-posting';
import { Button } from '@/components/ui/button';
import { TruncatedText } from '@/components/ui/truncated-text';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { cn } from '@/utils/classnames';
import { Skeleton } from '@/components/ui/skeleton';

interface OpeningRowProps {
  openingId: string;
}

export function OpeningRow({ openingId }: OpeningRowProps) {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(true);
  const { formatCurrency } = useCurrencySymbol();
  const { confirm } = useConfirmation();
  const { title, selectedChannels, totalBudget } = useOpening(openingId);

  const { selectedOpeningIds } = useJobPostingContext();
  const isOnlyOpening = selectedOpeningIds.length === 1;

  const handleRemoveOpening = useRemoveOpeningFromPosting(openingId);

  const handleRemoveClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = await confirm({
      title: intl.formatMessage(
        {
          defaultMessage: 'Remove "{openingName}"?',
          description:
            'Modal title when removing a specific opening from the job posting',
        },
        { openingName: title },
      ),
      description: intl.formatMessage(
        {
          defaultMessage:
            'This will remove "{openingName}" from the posting and update the total budget accordingly.',
          description:
            'Confirm dialog body explaining the effect of removing an opening',
        },
        { openingName: title },
      ),
      confirmLabel: intl.formatMessage({
        defaultMessage: 'Remove opening',
        description:
          'Primary button to confirm removing an opening from the posting',
      }),
      confirmVariant: 'destructive',
    });

    if (confirmed) handleRemoveOpening();
  };

  return (
    <li className="flex flex-col">
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        className="hover:bg-fx-neutral-50 flex h-12 items-center justify-between gap-2 p-3 pb-2"
      >
        {/* Opening name */}
        {title ? (
          <TruncatedText variant="body2" as="span" className="min-w-0">
            {title}
          </TruncatedText>
        ) : (
          <Skeleton className="h-4 w-full" />
        )}

        <div className="flex shrink-0 items-center gap-1">
          {/* Total opening budget */}
          {totalBudget > 0 && (
            <Typography
              variant="subtitle1"
              className="text-fx-neutral-900 font-bold"
            >
              {formatCurrency(totalBudget)}
            </Typography>
          )}

          {/* Remove opening button */}
          {!isOnlyOpening && title && (
            <Button
              type="button"
              variant="icon"
              size="icon"
              className="h-7 w-7"
              onClick={handleRemoveClick}
            >
              <TrashIcon />
            </Button>
          )}

          {/* Expand/collapse icon */}
          <img
            src={ChevronDownIcon}
            alt=""
            className={cn('transition-transform', isExpanded && 'rotate-180')}
          />
        </div>
      </button>

      {isExpanded && (
        <>
          {selectedChannels.length === 0 ? (
            <Typography
              variant="small1"
              className="text-fx-neutral-600 p-3 pt-0 italic"
            >
              <FormattedMessage
                defaultMessage="No job board selected yet"
                description="Sub-text on the selected-opening row when no channels have been chosen"
              />
            </Typography>
          ) : (
            <ul className="flex flex-col gap-2 p-3 pt-0">
              {selectedChannels.map(channel => (
                <ChannelRow
                  key={`${openingId}-${channel.slug}`}
                  channel={channel}
                  openingId={openingId}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}
